import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionButton } from '../Forms/ActionButton';
import { useDispatch, useSelector } from 'react-redux';
import { LOGOUT } from '../../features/partner/partnerSlice';
import { useHeader } from '../../context/HeaderContext';
import { FaUser } from "react-icons/fa";
import { MdOutlineNotifications, MdRefresh } from 'react-icons/md';
import { getPartnerName } from '../../utils/partnerInfo';
import { useGetPartnerNotifications, useMarkNotificationAsRead, useMarkAllNotificationsAsRead } from '../../queries/notifications';

export default function PartnerHeader() {
  const { headerTitle } = useHeader();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split('/').pop();
  const { partnerInfo } = useSelector((state) => state.partner);

  // Notification hooks
  const { data: notificationsData, isLoading: notificationsLoading, refetch: refetchNotifications, isRefetching } = useGetPartnerNotifications({ limit: 10 });
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  const notifications = notificationsData?.data || [];
  const unreadCount = notificationsData?.pagination?.unreadCount || 0;

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsReadMutation.mutateAsync(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleLogout = async () => {
    const conf = await new Promise((resolve) => {
      const dialog = document.createElement('div');
      dialog.className = 'fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50';

      dialog.innerHTML = `
        <div class="bg-white p-6 rounded-md shadow-md text-center">
          <p class="mb-4 text-lg">Are you sure you want to logout?</p>
          <div class="flex justify-center gap-4">
          <button id="confirm-yes" class="px-4 py-2 text-base bg-red-500 text-white rounded-md bg-red font-bold">Yes</button>
          <button id="confirm-no" class="px-4 py-2 text-base bg-gray-300 rounded-md border-2 border-red font-bold">No</button>
          </div>
        </div>
      `;

      document.body.appendChild(dialog);

      dialog.querySelector('#confirm-yes').addEventListener('click', () => {
        resolve(true);
        document.body.removeChild(dialog);
      });

      dialog.querySelector('#confirm-no').addEventListener('click', () => {
        resolve(false);
        document.body.removeChild(dialog);
      });
    });
    if (conf) {
      dispatch(LOGOUT());
      sessionStorage.removeItem('partner-t');
      sessionStorage.removeItem('partner-info');
      localStorage.removeItem('partner-t');
      localStorage.removeItem('partner-info');
    }
  }

  return (
    <header className="flex sticky top-0 items-center flex-wrap justify-between gap-2 py-2.5 pr-9 pl-20 z-40 bg-white shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:px-5">
      <h2 className='capitalize ml-48 font-bold text-primary text-xl'>
        {headerTitle || currentPath.split("-").map(word => word + " ")}
      </h2>
      <div className='flex gap-3 mr-10'>
        <div className='group/item relative' ref={notificationRef}>
          <div className="relative cursor-pointer" onClick={toggleNotifications}>
            <div className='p-1.5 rounded-full bg-[#F0F2F5]'>
              <MdOutlineNotifications className='size-5 fill-primaryHoverDark' />
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-navBtn text-primary text-sm rounded-full w-5 h-5 flex items-center justify-center font-extrabold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>

          {showNotifications && (
            <div className='absolute right-0 top-full mt-2 w-96 max-h-96 bg-white border border-primary rounded-lg shadow-lg overflow-hidden z-50'>
              <div className='sticky top-0 bg-white p-4 border-b border-primary flex justify-between items-center'>
                <h3 className='font-semibold text-base'>
                  Notifications
                  <MdRefresh
                    className={`fill-secondary hover:fill-black inline ml-1 mb-1 cursor-pointer ${notificationsLoading || isRefetching ? 'animate-spin' : ''}`}
                    onClick={() => refetchNotifications()}
                  />
                </h3>
                {notifications.length > 0 && unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={markAllAsReadMutation.isPending}
                    className='px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primaryHoverDark disabled:opacity-50 font-bold'
                  >
                    {markAllAsReadMutation.isPending ? 'Loading...' : 'Mark All Read'}
                  </button>
                )}
              </div>

              <div className='max-h-80 overflow-y-auto'>
                {notificationsLoading || isRefetching ? (
                  <div className='p-4 text-base text-center'>Loading notifications...</div>
                ) : notifications.length === 0 ? (
                  <div className='p-4 text-base text-center'>No notifications found</div>
                ) : (
                  <>
                    {notifications.map((notification) => (
                      <div
                        key={notification._id}
                        className={`p-4 border-b border-border ${!notification.isRead ? 'bg-border' : 'bg-white'} hover:bg-gray-50 transition-colors`}
                      >
                        <div className='flex justify-between items-start gap-2'>
                          <div className='flex-1'>
                            <div className='flex items-center gap-2'>
                              <h4 className={`font-semibold text-base ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                                {notification.title}
                              </h4>
                              {!notification.isRead && (
                                <span className='w-2 h-2 bg-primary rounded-full flex-shrink-0'></span>
                              )}
                            </div>
                            <div className='flex items-center justify-between'>
                              <p className='text-sm text-secondary'>
                                {formatDate(notification.createdAt)}
                              </p>
                            </div>
                            <p className='text-base mt-1'>
                              {notification.message}
                            </p>
                          </div>
                          {!notification.isRead && (
                            <button
                              onClick={() => handleMarkAsRead(notification._id)}
                              disabled={markAsReadMutation.isPending}
                              className='px-2 py-1 text-sm bg-primary text-white rounded hover:bg-primaryHoverDark disabled:opacity-50 flex-shrink-0 font-bold'
                            >
                              Mark Read
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <div className='group/item relative'>
          <div className='p-2.5 rounded-full bg-[#F0F2F5] cursor-pointer'>
            <FaUser className='size-3.5 fill-primaryHoverDark' />
          </div>
          <div className='group/edit invisible group-hover/item:visible border border-primary bg-white rounded-md absolute w-72 right-0 shadow-lg z-50'>
            <div className='p-4 pb-2'>
              <div className='text-sm text-gray-600 mb-1'>Logged in as:</div>
              <div className='text-base font-semibold text-gray-900 mb-3 max-w-48 truncate' title={`Partner ${getPartnerName()}`}>
                Partner {getPartnerName() || 'User'}
              </div>
            </div>
            <div className='border-t border-border px-4 py-3'>
              <button
                onClick={() => navigate('/settings/profile')}
                className='w-full text-left text-base font-semibold text-gray-900 hover:text-primary py-2 transition-colors'
              >
                My Profile
              </button>
              <button
                onClick={() => navigate('/settings/change-password')}
                className='w-full text-left text-base font-semibold text-gray-900 hover:text-primary py-2 transition-colors'
              >
                Change Password
              </button>
            </div>
            <div className='flex justify-end items-center border-t border-border px-4 py-4'>
              <ActionButton
                text={'Logout'}
                variant={'submit'}
                handleSubmit={handleLogout}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
